import { distance, nearestPointOnShape, projectOntoCourse, signedDistanceToShape } from '@sw/geo'
import { detectGateCrossing } from '@sw/geo'
import type {
  Action,
  EmitterRuntimeState,
  Pose,
  Rule,
  Trigger,
  TriggerType,
  UnsequencedEvent,
  Vec2,
} from '@sw/schema'
import type { CompiledWorld } from './compile.js'
import { triggerKey } from './compile.js'
import type { Role } from './conditions.js'
import { compareValues, evaluateConditions } from './conditions.js'
import { calendarPartsInZone } from './cron.js'
import { createLatch, updateLatch } from './latch.js'
import type { EngineState, Firing, TriggerState } from './state.js'
import { createRuleState } from './state.js'

/** Below this, a listener counts as standing still. */
const STILL_SPEED_MPS = 0.5

/** How long firings stay available to chained and sequence triggers. */
const FIRING_MEMORY_MS = 120_000

export interface CueSignal {
  cueId: string
  atMs: number
}

export interface EmitterSignal {
  emitterId: string
  state: EmitterRuntimeState
  atMs: number
}

export interface TickContext {
  /** Session clock, milliseconds since the session began. */
  nowMs: number
  /** Epoch milliseconds, needed only for scheduled triggers. */
  wallClockMs?: number
  pose?: Pose
  previousPose?: Pose
  /**
   * True when positioning has become untrustworthy. Spatial triggers then hold
   * their current state rather than firing on noise — a listener under a
   * bridge should hear nothing new, not everything at once.
   */
  accuracyDegraded?: boolean
  localMinuteOfDay?: number
  roles?: readonly Role[]
  /** Live positions for emitters that move, such as a path in flight. */
  emitterPositions?: ReadonlyMap<string, Vec2>
  cues?: readonly CueSignal[]
  emitterSignals?: readonly EmitterSignal[]
  /** Firings from the other engine, so chains can cross the network. */
  externalFirings?: readonly Firing[]
  sessionEnding?: boolean
  /** Injectable so a replay reproduces the same rolls. */
  random?: () => number
}

export interface DispatchedAction {
  ruleId: string
  action: Action
  atMs: number
}

export interface TickResult {
  /** Rules that fired this tick, for logging and for the other engine. */
  firings: Firing[]
  /** Actions due now, in authored order. The host performs the audible ones. */
  dispatched: DispatchedAction[]
  events: UnsequencedEvent[]
}

function speedOf(ctx: TickContext): number | undefined {
  if (ctx.pose?.speedMps !== undefined) return ctx.pose.speedMps
  if (ctx.pose && ctx.previousPose) {
    const dt = (ctx.pose.t - ctx.previousPose.t) / 1000
    if (dt > 0) return distance(ctx.previousPose.position, ctx.pose.position) / dt
  }
  return undefined
}

/**
 * Where an emitter is for the purpose of a proximity trigger. A zone resolves
 * to its nearest point rather than its centre, so an L-shaped courtyard is
 * "near" when you are beside it rather than when you are near the empty corner
 * its centroid happens to land in.
 */
function emitterPosition(
  world: CompiledWorld,
  ctx: TickContext,
  emitterId: string,
): Vec2 | undefined {
  const live = ctx.emitterPositions?.get(emitterId)
  if (live) return live

  const emitter = world.emitters.get(emitterId)
  if (!emitter) return undefined
  if (emitter.kind === 'point') return emitter.position
  if (emitter.kind === 'zone') {
    const region = world.regions.get(emitter.regionId)
    if (!region || !ctx.pose) return undefined
    return nearestPointOnShape(ctx.pose.position, region.shape)
  }
  // Paths and companions only have a position while the audio engine is
  // rendering them; it supplies one through emitterPositions.
  return undefined
}

function consumeSignals<T extends { atMs: number }>(
  signals: readonly T[] | undefined,
  triggerState: TriggerState,
  matches: (signal: T) => boolean,
): boolean {
  if (!signals || signals.length === 0) return false
  const since = triggerState.lastSeenMs ?? Number.NEGATIVE_INFINITY
  let hit = false
  let newest = since
  for (const signal of signals) {
    if (signal.atMs <= since) continue
    newest = Math.max(newest, signal.atMs)
    if (matches(signal)) hit = true
  }
  if (newest > since) triggerState.lastSeenMs = newest
  return hit
}

function evaluateTrigger(
  world: CompiledWorld,
  state: EngineState,
  ts: TriggerState,
  rule: Rule,
  trigger: Trigger,
  triggerIndex: number,
  ctx: TickContext,
): boolean {
  const now = ctx.nowMs
  const pose = ctx.pose
  const spatialUsable = pose !== undefined && ctx.accuracyDegraded !== true

  switch (trigger.type) {
    case 'zone': {
      if (!spatialUsable) return false
      const region = world.regions.get(trigger.regionId)
      if (!region) return false
      ts.latch ??= createLatch()
      const result = updateLatch(
        ts.latch,
        signedDistanceToShape(pose.position, region.shape),
        { exitMarginM: trigger.exitMarginM, debounceMs: trigger.debounceMs },
        now,
      )
      return trigger.transition === 'enter' ? result.entered : result.exited
    }

    case 'proximity': {
      if (!spatialUsable) return false
      const target =
        trigger.target.kind === 'point'
          ? trigger.target.position
          : emitterPosition(world, ctx, trigger.target.emitterId)
      if (!target) return false
      ts.latch ??= createLatch()
      const result = updateLatch(
        ts.latch,
        distance(pose.position, target) - trigger.radiusM,
        { exitMarginM: trigger.exitMarginM, debounceMs: trigger.debounceMs },
        now,
      )
      return trigger.transition === 'enter' ? result.entered : result.exited
    }

    case 'dwell': {
      if (!spatialUsable) return false
      const region = world.regions.get(trigger.regionId)
      if (!region) return false
      ts.latch ??= createLatch()
      const result = updateLatch(
        ts.latch,
        signedDistanceToShape(pose.position, region.shape),
        { exitMarginM: trigger.exitMarginM, debounceMs: trigger.debounceMs },
        now,
      )
      if (result.entered) {
        ts.insideSinceMs = now
        ts.dwellFired = false
      }
      if (result.exited) {
        ts.insideSinceMs = undefined
        ts.dwellFired = false
      }
      if (
        ts.latch.inside &&
        ts.insideSinceMs !== undefined &&
        ts.dwellFired !== true &&
        now - ts.insideSinceMs >= trigger.durationMs
      ) {
        ts.dwellFired = true
        return true
      }
      return false
    }

    case 'gate': {
      if (!spatialUsable || !ctx.previousPose) return false
      const gate = world.gates.get(trigger.gateId)
      if (!gate) return false
      if (ts.lastFiredMs !== undefined && now - ts.lastFiredMs < trigger.debounceMs) return false
      const crossing = detectGateCrossing(gate, ctx.previousPose.position, pose.position)
      if (!crossing) return false
      if (trigger.direction !== 'either' && trigger.direction !== crossing) return false
      ts.lastFiredMs = now
      return true
    }

    case 'chainage': {
      if (!spatialUsable) return false
      const course = world.courses.get(trigger.courseId)
      if (!course) return false
      const fix = projectOntoCourse(course, pose.position, {
        previousChainageM: ts.previousChainageM,
      })
      const previous = ts.previousChainageM
      ts.previousChainageM = fix.chainageM
      if (previous === undefined || !fix.onCourse) return false

      const forward = previous < trigger.distanceM && fix.chainageM >= trigger.distanceM
      const backward = previous > trigger.distanceM && fix.chainageM <= trigger.distanceM
      if (trigger.direction === 'forward') return forward
      if (trigger.direction === 'backward') return backward
      return forward || backward
    }

    case 'speed': {
      const speed = speedOf(ctx)
      if (speed === undefined) return false
      const holds =
        trigger.comparison === 'above' ? speed > trigger.speedMps : speed < trigger.speedMps
      if (!holds) {
        ts.conditionSinceMs = undefined
        ts.sustainedFired = false
        return false
      }
      ts.conditionSinceMs ??= now
      if (ts.sustainedFired !== true && now - ts.conditionSinceMs >= trigger.sustainedMs) {
        ts.sustainedFired = true
        return true
      }
      return false
    }

    case 'motion': {
      const speed = speedOf(ctx)
      if (speed === undefined) return false
      const still = speed <= STILL_SPEED_MPS

      if (trigger.state === 'stopped') {
        if (!still) {
          ts.conditionSinceMs = undefined
          ts.sustainedFired = false
          return false
        }
        ts.conditionSinceMs ??= now
        if (ts.sustainedFired !== true && now - ts.conditionSinceMs >= trigger.afterMs) {
          ts.sustainedFired = true
          return true
        }
        return false
      }

      // Resuming only means anything after a spell of standing still.
      const wasStill = ts.previousTruth === true
      if (still) {
        ts.conditionSinceMs ??= now
        if (now - ts.conditionSinceMs >= trigger.afterMs) ts.previousTruth = true
        return false
      }
      ts.conditionSinceMs = undefined
      ts.previousTruth = false
      return wasStill
    }

    case 'elapsed': {
      if (ts.fired === true) return false
      if (now - state.startedAtMs < trigger.sinceStartMs) return false
      ts.fired = true
      return true
    }

    case 'wallClock': {
      if (ctx.wallClockMs === undefined) return false
      const matcher = world.cron.get(triggerKey(rule.id, triggerIndex))
      if (!matcher) return false
      const minuteIndex = Math.floor(ctx.wallClockMs / 60_000)
      if (ts.lastMinuteIndex === minuteIndex) return false
      if (!matcher(calendarPartsInZone(ctx.wallClockMs, trigger.timezone))) return false
      ts.lastMinuteIndex = minuteIndex
      return true
    }

    case 'sequence': {
      const since = ts.lastSeenMs ?? Number.NEGATIVE_INFINITY
      let hit = false
      for (const firing of state.firings) {
        if (firing.atMs <= since) continue
        ts.lastSeenMs = Math.max(ts.lastSeenMs ?? Number.NEGATIVE_INFINITY, firing.atMs)

        if (
          ts.sequenceStartedMs !== undefined &&
          trigger.withinMs !== undefined &&
          firing.atMs - ts.sequenceStartedMs > trigger.withinMs
        ) {
          ts.sequenceProgress = 0
          ts.sequenceStartedMs = undefined
        }

        const expected = ts.sequenceProgress ?? 0
        if (firing.ruleId === trigger.ruleIds[expected]) {
          if (expected === 0) ts.sequenceStartedMs = firing.atMs
          ts.sequenceProgress = expected + 1
          if (ts.sequenceProgress >= trigger.ruleIds.length) {
            ts.sequenceProgress = 0
            ts.sequenceStartedMs = undefined
            hit = true
          }
        } else if (firing.ruleId === trigger.ruleIds[0]) {
          // Starting over rather than losing the run entirely.
          ts.sequenceProgress = 1
          ts.sequenceStartedMs = firing.atMs
        }
        // Unrelated firings are ignored: in a busy world most firings are.
      }
      return hit
    }

    case 'variable': {
      const truth = compareValues(
        state.variables[trigger.name],
        trigger.comparison,
        trigger.value,
      )
      const rising = truth && ts.previousTruth !== true
      ts.previousTruth = truth
      return rising
    }

    case 'random': {
      const last = ts.lastSeenMs
      ts.lastSeenMs = now
      if (last === undefined) return false
      if (ts.lastFiredMs !== undefined && now - ts.lastFiredMs < trigger.minIntervalMs) return false

      const dtSeconds = Math.max(0, (now - last) / 1000)
      if (dtSeconds === 0) return false
      // Convert a per-second chance into a per-tick one, so behaviour does not
      // change when the tick rate does.
      const chance = 1 - (1 - trigger.chancePerSecond) ** dtSeconds
      const roll = (ctx.random ?? Math.random)()
      if (roll >= chance) return false
      ts.lastFiredMs = now
      return true
    }

    case 'ruleFired':
      return consumeSignals(state.firings, ts, (firing) => firing.ruleId === trigger.ruleId)

    case 'emitterState':
      return consumeSignals(
        ctx.emitterSignals,
        ts,
        (signal) => signal.emitterId === trigger.emitterId && signal.state === trigger.state,
      )

    case 'operatorCue':
      return consumeSignals(ctx.cues, ts, (cue) => cue.cueId === trigger.cueId)

    case 'accuracy': {
      const degraded = ctx.accuracyDegraded === true
      const changed = degraded !== state.previousAccuracyDegraded
      if (!changed) return false
      return trigger.state === 'degraded' ? degraded : !degraded
    }

    case 'session': {
      if (trigger.event === 'end') return ctx.sessionEnding === true
      if (ts.fired === true) return false
      ts.fired = true
      return true
    }
  }
}

function withinFireLimit(rule: Rule, fireCount: number): boolean {
  switch (rule.fireLimit.kind) {
    case 'unlimited':
      return true
    case 'once':
    case 'oncePerSession':
    case 'oncePerListener':
      return fireCount < 1
    case 'count':
      return fireCount < rule.fireLimit.n
  }
}

/**
 * Applies the actions the engine owns. Everything else — playing, fading,
 * moving sound — belongs to the host, which is the only part that knows about
 * audio.
 */
function applyEngineAction(
  state: EngineState,
  action: Action,
  nowMs: number,
  events: UnsequencedEvent[],
): void {
  if (action.type === 'setVariable') {
    const current = state.variables[action.name]
    const next =
      action.assign.op === 'set'
        ? action.assign.value
        : (typeof current === 'number' ? current : 0) + action.assign.value
    state.variables[action.name] = next
    events.push({
      type: 'variableChanged',
      ts: nowMs,
      scope: state.scope,
      name: action.name,
      value: next,
    })
    return
  }

  if (action.type === 'setRuleEnabled') {
    for (const ruleId of action.ruleIds) {
      const ruleState = state.rules[ruleId]
      if (ruleState) ruleState.enabled = action.enabled
    }
  }
}

function dispatchDue(
  state: EngineState,
  nowMs: number,
  events: UnsequencedEvent[],
  dispatched: DispatchedAction[],
): void {
  if (state.pending.length === 0) return

  const due = state.pending.filter((pending) => pending.atMs <= nowMs)
  if (due.length === 0) return
  state.pending = state.pending.filter((pending) => pending.atMs > nowMs)

  due.sort((a, b) => a.atMs - b.atMs || a.orderInRule - b.orderInRule)
  for (const pending of due) {
    applyEngineAction(state, pending.action, nowMs, events)
    dispatched.push({ ruleId: pending.ruleId, action: pending.action, atMs: pending.atMs })
    events.push({
      type: 'actionExecuted',
      ts: nowMs,
      scope: state.scope,
      ruleId: pending.ruleId,
      action: pending.action,
    })
  }
}

/**
 * One evaluation step. Pure in spirit: everything it needs arrives in `ctx`,
 * everything it decides comes back in the result, and the only thing it
 * touches is the state object handed to it. That is what lets the same code
 * run on a phone and on the server, and lets a recorded trace be replayed to
 * the same outcome.
 */
export function tick(world: CompiledWorld, state: EngineState, ctx: TickContext): TickResult {
  const now = ctx.nowMs
  const events: UnsequencedEvent[] = []
  const dispatched: DispatchedAction[] = []
  const firings: Firing[] = []

  if (ctx.externalFirings?.length) {
    state.firings.push(...ctx.externalFirings)
    state.firings.sort((a, b) => a.atMs - b.atMs)
  }

  // Actions that came due since the last tick run first, so a variable set a
  // moment ago is visible to the rules evaluated below.
  dispatchDue(state, now, events, dispatched)

  const conditionContext = {
    variables: state.variables,
    localMinuteOfDay: ctx.localMinuteOfDay,
    roles: ctx.roles ?? (['listener'] as const),
  }

  for (const rule of world.rulesByScope[state.scope]) {
    const ruleState = (state.rules[rule.id] ??= createRuleState(rule))
    if (!ruleState.enabled) continue

    let firedBy: TriggerType | undefined
    rule.triggers.forEach((trigger, index) => {
      const triggerState = (ruleState.triggers[index] ??= {})
      if (evaluateTrigger(world, state, triggerState, rule, trigger, index, ctx)) {
        ruleState.lastHitMs[index] = now
        firedBy ??= trigger.type
      }
    })

    if (firedBy === undefined) continue

    if (rule.triggerMode === 'all') {
      const allRecent = ruleState.lastHitMs.every(
        (hit) => hit !== undefined && now - hit <= rule.triggerWindowMs,
      )
      if (!allRecent) continue
    }

    if (!withinFireLimit(rule, ruleState.fireCount)) continue
    if (ruleState.lastFiredAtMs !== undefined && now - ruleState.lastFiredAtMs < rule.cooldownMs) {
      continue
    }
    if (!evaluateConditions(rule.conditions, conditionContext)) continue

    ruleState.fireCount += 1
    ruleState.lastFiredAtMs = now
    if (rule.triggerMode === 'all') ruleState.lastHitMs.fill(undefined)

    firings.push({ ruleId: rule.id, atMs: now })
    events.push({
      type: 'ruleFired',
      ts: now,
      scope: state.scope,
      ruleId: rule.id,
      triggerType: firedBy,
    })

    rule.actions.forEach((action, orderInRule) => {
      state.pending.push({ ruleId: rule.id, action, atMs: now + action.delayMs, orderInRule })
    })
  }

  // Undelayed actions run in the same tick that fired them, rather than
  // waiting for the next one — that latency is audible.
  dispatchDue(state, now, events, dispatched)

  // Firings become visible to chained triggers on the next tick, which is what
  // keeps a rule that triggers itself from looping forever inside one step.
  state.firings.push(...firings)
  const horizon = now - FIRING_MEMORY_MS
  if (state.firings.length > 0 && state.firings[0].atMs < horizon) {
    state.firings = state.firings.filter((firing) => firing.atMs >= horizon)
  }

  state.previousAccuracyDegraded = ctx.accuracyDegraded === true
  state.lastTickMs = now

  return { firings, dispatched, events }
}
